import React, { useState, useEffect } from "react";
import { X, Plus, Trash2, AlertTriangle } from "lucide-react";
import kitchenStationService from "../../../services/kitchenStationService";
import { useAuth } from '../../../context/AuthContext';
import { P } from '@/config/permissionCodes';


export default function KitchenStationManager({ branchId, onSave, onClose }) {
  const [stationList, setStationList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [stationToDelete, setStationToDelete] = useState(null);
  const { hasPermission } = useAuth();
const canAdd    = hasPermission(P.BACK_OFFICE.MENU.KITCHEN.ADD);
const canEdit   = hasPermission(P.BACK_OFFICE.MENU.KITCHEN.EDIT);
const canDelete = hasPermission(P.BACK_OFFICE.MENU.KITCHEN.DELETE);

  const loadStations = async () => {
    try {
      const res = await kitchenStationService.getAll();
      let stations = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : [];
      if (branchId) {
        stations = stations.filter(s => s.branchId === branchId);
      }
      setStationList(stations);
    } catch (error) {
      console.error("Error fetching kitchen stations:", error);
      setMessage(" Failed to fetch kitchen stations.");
    }
  };

  useEffect(() => {
    loadStations();
  }, []);

  const handleAddStation = () => {
    const newStation = {
      id: Date.now(),
      name: "",
      description: "",
      isActive: true,
      isNew: true,
    };
    setStationList([...stationList, newStation]);
  };

  const handleUpdateStation = (id, field, value) => {
    setStationList((prev) =>
      prev.map((s) =>
        s.id === id
          ? { ...s, [field]: value, isEdited: !s.isNew ? true : s.isEdited }
          : s
      )
    );
  };

  const confirmDelete = (id) => {
    setStationToDelete(id);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!stationToDelete) return;
    try {
      await kitchenStationService.delete(stationToDelete);
      setMessage(" Kitchen station deleted successfully!");
      await loadStations();
      onSave?.(null, "Kitchen station deleted successfully!");
    } catch (error) {
      console.error("Error deleting kitchen station:", error);
      setMessage(" Failed to delete kitchen station.");
    } finally {
      setShowDeleteDialog(false);
      setStationToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setStationToDelete(null);
  };

  const toggleActive = (id) => {
    setStationList((prev) =>
      prev.map((s) =>
        s.id === id
          ? { ...s, isActive: !s.isActive, isEdited: !s.isNew ? true : s.isEdited }
          : s
      )
    );
  };

  const handleSave = async () => {
    const validStations = stationList.filter((s) => s.name.trim() !== "");
    if (validStations.length === 0) {
      setMessage(" Please add at least one valid kitchen station.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const res = await kitchenStationService.getAll();
      const originalStations = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : [];


      const newStations = validStations.filter((s) => s.isNew);
      for (const s of newStations) {
        const payload = {
          name: s.name,
          description: s.description,
          isActive: s.isActive,
        };
        await kitchenStationService.create(payload);
      }

      const updatedStations = validStations.filter((s) => !s.isNew && s.isEdited);
      for (const s of updatedStations) {
        const payload = {
          name: s.name,
          description: s.description,
          isActive: s.isActive,
        };
        await kitchenStationService.update(s.id, payload);
      }

      await loadStations();

      const toggled = updatedStations.filter((s) => {
        const original = originalStations.find((o) => o.id === s.id);
        return original && original.isActive !== s.isActive;
      });

      const renamedOrEdited = updatedStations.filter((s) => {
        const original = originalStations.find((o) => o.id === s.id);
        return (
          original &&
          (original.name !== s.name || original.description !== s.description)
        );
      });

      let finalMsg = "";
      if (newStations.length > 0) {
        finalMsg = "Kitchen station added successfully!";
      } else if (toggled.length > 0) {
        const anyActivated = toggled.some((s) => s.isActive);
        finalMsg = anyActivated
          ? "Kitchen station activated successfully!"
          : "Kitchen station deactivated successfully!";
      } else if (renamedOrEdited.length > 0) {
        finalMsg = "Kitchen station updated successfully!";
      } else {
        finalMsg = "Kitchen stations saved successfully!";
      }

      setMessage(finalMsg);
      onSave?.(validStations, finalMsg);

      setTimeout(() => onClose(), 1000);
    } catch (error) {
      console.error("Error saving kitchen stations:", error);
      setMessage(" Failed to save kitchen stations.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden relative">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Manage Kitchen Stations
            </h2>
            <p className="text-xs text-gray-600 mt-0.5">
              Organize your tables by kitchen stations
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-md transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* List */}
        <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
          <div className="space-y-3">
            {stationList.map((station, index) => (
              <div
                key={station.id || index}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center justify-center w-8 h-8 bg-indigo-100 text-indigo-700 rounded font-semibold text-sm">
                  {index + 1}
                </div>

                <input
                  type="text"
                  value={station.name}
                  onChange={(e) => canEdit && handleUpdateStation(station.id, 'name', e.target.value)}
                  readOnly={!canEdit}

                  placeholder="Station name (e.g., Grill Section)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />

                <input
                  type="text"
                  value={station.description}
                  onChange={(e) => canEdit && handleUpdateStation(station.id, 'description', e.target.value)}
                  readOnly={!canEdit}
                  placeholder="Description (optional)"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={station.isActive}
                    onChange={() => toggleActive(station.id)}
                    className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-xs text-gray-600">Active</span>
                </label>
               {!station.isNew && canDelete && (
  <button onClick={() => confirmDelete(station.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors">
    <Trash2 size={16} />
  </button>
)}
              </div>
            ))}

            {canAdd && (
  <button onClick={handleAddStation} className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-indigo-500 hover:text-indigo-600 transition-colors flex items-center justify-center gap-2 text-sm font-medium">
    <Plus size={18} />
    <span>Add Kitchen Station</span>
  </button>
)}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-3 bg-gray-50 flex items-center justify-between">
          <p className="text-xs text-gray-600">
            {stationList.length} station{stationList.length !== 1 ? "s" : ""}
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors text-sm"
            >
              Cancel
            </button>
            {(canAdd || canEdit) && (
  <button onClick={handleSave} disabled={loading} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors text-sm">
    {loading ? 'Saving...' : 'Save Stations'}
  </button>
)}
          </div>
        </div>

        {message && (
          <div className="px-6 py-2 text-sm text-gray-700 text-center">
            {message}
          </div>
        )}

        {showDeleteDialog && (
          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 shadow-lg w-80 text-center">
              <AlertTriangle size={40} className="text-red-500 mx-auto mb-2" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Delete Kitchen Station?
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete this kitchen station? This will permanently remove it.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={handleCancelDelete}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
